/**
 * The approver's signing key — and the only cryptography in this console.
 *
 * An escalation is resolved by a HUMAN's Ed25519 signature over the exact decision. That is
 * the whole point of the mechanism: the record says *this person* approved *this amount to
 * this recipient*, and it is verifiable by a stranger with nothing but the event body. So the
 * private key has to live on the approver's device and sign locally — a server that could
 * produce the signature itself would make the signature worthless as evidence.
 *
 * Hence: the key is generated in the browser, kept in localStorage, and never sent anywhere.
 * authoxi only ever sees the public half, as a `did:key` inside the approver's mandate.
 *
 * Three details are load-bearing, and getting any of them wrong yields a signature the server
 * rejects with a 403 that says nothing useful:
 *
 *  1. **What gets signed is the SHA-256 DIGEST of the canonical bytes, not the bytes.**
 *     `events.signing_input()` is `sha256(JCS(body))` and the private key signs that digest.
 *     The API hands us `to_sign` = base64url(canonical bytes), so we must hash before signing.
 *  2. **The signature is `"ed25519:" + hex`**, not raw base64.
 *  3. **`did:key` is base58btc(0xed01 ‖ pubkey) with a `z` prefix** — multicodec, not the raw key.
 */
import { ed25519 } from '@noble/curves/ed25519'
import { sha256 } from '@noble/hashes/sha256'
import { base58, base64urlnopad } from '@scure/base'
import { bytesToHex } from '@noble/hashes/utils'

const STORAGE = 'authoxi.approver.sk'
const ED25519_MULTICODEC = Uint8Array.from([0xed, 0x01])

export interface ApproverKey {
  privHex: string
  publicKeyB64: string
  did: string
}

const didKeyFromPub = (pub: Uint8Array): string => {
  const prefixed = new Uint8Array(ED25519_MULTICODEC.length + pub.length)
  prefixed.set(ED25519_MULTICODEC)
  prefixed.set(pub, ED25519_MULTICODEC.length)
  return 'did:key:z' + base58.encode(prefixed)
}

const fromPriv = (priv: Uint8Array): ApproverKey => {
  const pub = ed25519.getPublicKey(priv)
  return {
    privHex: bytesToHex(priv),
    publicKeyB64: base64urlnopad.encode(pub),
    did: didKeyFromPub(pub),
  }
}

/** The approver key held by THIS browser, if any. */
export function loadApproverKey(): ApproverKey | null {
  const hex = localStorage.getItem(STORAGE)
  if (!hex) return null
  try {
    return fromPriv(hexToBytes(hex))
  } catch {
    return null
  }
}

/** Mint a new approver key. The private half never leaves this device. */
export function createApproverKey(): ApproverKey {
  const priv = ed25519.utils.randomPrivateKey()
  localStorage.setItem(STORAGE, bytesToHex(priv))
  return fromPriv(priv)
}

export function forgetApproverKey(): void {
  localStorage.removeItem(STORAGE)
}

/**
 * Sign one resolution.
 *
 * `toSignB64` is the API's `to_sign[resolution]` — base64url of the canonical bytes of the
 * exact event this human is attesting to. We hash, sign the digest, and hex it.
 *
 * The signature binds THIS decision: a different amount or a different recipient produces
 * different canonical bytes, so it cannot be replayed onto another payment.
 */
export function signResolution(key: ApproverKey, toSignB64: string): string {
  const canonicalBytes = base64urlnopad.decode(stripPad(toSignB64))
  const digest = sha256(canonicalBytes) // ← the wire contract: sign the digest
  const sig = ed25519.sign(digest, hexToBytes(key.privHex))
  return 'ed25519:' + bytesToHex(sig)
}

/** The server pads its base64url; @scure's nopad codec does not want the '='. */
const stripPad = (s: string) => s.replace(/=+$/, '')

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return out
}
